import React, { useRef, useImperativeHandle, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  IFButton,
  IFDialog,
  IFModal,
  IFSelect,
  IFText,
  IFTextInput,
  TariffElementsComponent,
} from 'Components'
import PropTypes from 'prop-types'
import Styles from './TariffModal.module.css'
import { Colors } from 'Theme'
import { DatePicker } from 'rsuite'
import validator from 'validator'
import InfinityEnums from 'Enums/InfinityEnums'
import RequestState from 'Enums/RequestState'
import {
  normalizeElements,
  isFormValid,
  normalizeTariffForCompare,
} from 'Utils/TariffOperations'
import { useOverlayScrollbars } from 'overlayscrollbars-react'

const TariffModal = React.forwardRef(
  (
    {
      onDismiss,
      onSubmit,
      createTariffRequestState,
      row,
      isEditing,
      updateTariffRequestState,
      handleDelete,
      deleteTariffRequestState,
      operator,
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const modalRef = useRef()
    const customDialogContentStyle = {
      height: '7200px',
      width: '750px',
    }
    const [name, setName] = useState(null)
    const [type, setType] = useState(null)
    const [startDate, setStartDate] = useState(null)
    const [expiryDate, setExpiryDate] = useState(null)
    const [vat, setVat] = useState(null)
    const [minPriceInclVat, setMinPriceInclVat] = useState(null)
    const [maxPriceInclVat, setMaxPriceInclVat] = useState(null)
    const [minPriceExclVat, setMinPriceExclVat] = useState(null)
    const [maxPriceExclVat, setMaxPriceExclVat] = useState(null)
    const [elements, setElements] = useState([])

    const [nameError, setNameError] = useState(null)
    const [vatError, setVatError] = useState(null)
    const [minPriceExclVatError, setMinPriceExclVatError] = useState(null)
    const [maxPriceExclVatError, setMaxPriceExclVatError] = useState(null)
    const [elementsError, setElementsError] = useState(false)
    const [dateError, setDateError] = useState(null)
    const [priceError, setPriceError] = useState(null)

    const deleteModalRef = useRef(null)
    const options = {
      scrollbars: {
        visibility: 'auto',
        autoHide: 'move',
        autoHideDelay: 1000,
      },
    }
    const [initialize, osInstance] = useOverlayScrollbars({
      options,
      defer: true,
    })
    const rootRef = useRef(null)
    const [scroller, setScroller] = useState(null)
    const [hasChanges, setHasChanges] = useState(false)
    useEffect(() => {
      const { current: root } = rootRef
      if (scroller && root) {
        initialize({
          target: root,
          elements: {
            viewport: scroller,
          },
        })
      }
      return () => osInstance()?.destroy()
    }, [scroller, initialize, osInstance])

    const calculateVat = (exclVat, inclVat) => {
      if (exclVat && inclVat && exclVat > 0) {
        const vatPercentage = ((inclVat - exclVat) / exclVat) * 100
        return vatPercentage.toFixed(1)
      }
      return null
    }
    const originalValuesRef = useRef(null)

    useEffect(() => {
      if (row) {
        const startDateValue = row.startDate ? new Date(row.startDate) : null
        const expiryDateValue = row.expiryDate ? new Date(row.expiryDate) : null
        const minPriceInclVatValue = row.minPrice?.inclVat ?? null
        const maxPriceInclVatValue = row.maxPrice?.inclVat ?? null
        const minPriceExclVatValue = row.minPrice?.exclVat ?? null
        const maxPriceExclVatValue = row.maxPrice?.exclVat ?? null
        const elementsValue = row.elements || []

        let calculatedVat = null
        if (row.minPrice?.exclVat && row.minPrice?.inclVat) {
          calculatedVat = calculateVat(
            row.minPrice.exclVat,
            row.minPrice.inclVat,
          )
        } else if (row.maxPrice?.exclVat && row.maxPrice?.inclVat) {
          calculatedVat = calculateVat(
            row.maxPrice.exclVat,
            row.maxPrice.inclVat,
          )
        }

        setName(row.name || null)
        setType(row.type || null)
        setStartDate(startDateValue)
        setExpiryDate(expiryDateValue)
        setMinPriceInclVat(minPriceInclVatValue)
        setMaxPriceInclVat(maxPriceInclVatValue)
        setMinPriceExclVat(minPriceExclVatValue)
        setMaxPriceExclVat(maxPriceExclVatValue)
        setElements(elementsValue)
        setVat(calculatedVat)

        originalValuesRef.current = {
          name: row.name || null,
          type: row.type || null,
          startDate: startDateValue,
          expiryDate: expiryDateValue,
          minPriceInclVat: minPriceInclVatValue,
          maxPriceInclVat: maxPriceInclVatValue,
          minPriceExclVat: minPriceExclVatValue,
          maxPriceExclVat: maxPriceExclVatValue,
          elements: elementsValue,
          vat: calculatedVat,
        }
      } else {
        originalValuesRef.current = null
      }
    }, [row])
    useEffect(() => {
      setPriceError(null)
      setDateError(null)

      if (startDate && expiryDate && startDate >= expiryDate) {
        setDateError(t('TariffModal.Invalid'))
      }

      const hasMinPrice =
        minPriceExclVat !== null &&
        minPriceExclVat !== '' &&
        minPriceExclVat !== undefined
      const hasMaxPrice =
        maxPriceExclVat !== null &&
        maxPriceExclVat !== '' &&
        maxPriceExclVat !== undefined

      if (hasMinPrice && hasMaxPrice) {
        const minNum = Number(minPriceExclVat)
        const maxNum = Number(maxPriceExclVat)

        if (
          !Number.isNaN(minNum) &&
          !Number.isNaN(maxNum) &&
          minNum >= maxNum
        ) {
          setPriceError(t('TariffModal.Invalid'))
        }
      }
    }, [startDate, expiryDate, minPriceExclVat, maxPriceExclVat, t])

    useEffect(() => {
      if (!isEditing) {
        setHasChanges(true)
        return
      }
      if (!originalValuesRef.current) {
        setHasChanges(false)
        return
      }
      const requiredFieldsFilled = !!(name && type && isFormValid(elements))

      if (!requiredFieldsFilled) {
        setHasChanges(false)
        return
      }
      const currentComparable = normalizeTariffForCompare({
        name,
        type,
        startDate,
        expiryDate,
        minPriceInclVat,
        maxPriceInclVat,
        minPriceExclVat,
        maxPriceExclVat,
        elements,
        vat,
      })
      const originalComparable = normalizeTariffForCompare(
        originalValuesRef.current,
      )
      const changed =
        JSON.stringify(currentComparable) !== JSON.stringify(originalComparable)
      setHasChanges(changed)
    }, [
      isEditing,
      name,
      type,
      startDate,
      expiryDate,
      minPriceInclVat,
      maxPriceInclVat,
      minPriceExclVat,
      maxPriceExclVat,
      elements,
      vat,
    ])
    const startOfDay = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate())
    const isPastDay = (d) => startOfDay(d) < startOfDay(new Date())

    const loadData = () => {
      if (row) {
        setName(row.name || null)
        setType(row.type || null)
        setStartDate(row.startDate ? new Date(row.startDate) : null)
        setExpiryDate(row.expiryDate ? new Date(row.expiryDate) : null)
        setMinPriceInclVat(row.minPrice?.inclVat || null)
        setMaxPriceInclVat(row.maxPrice?.inclVat || null)
        setMinPriceExclVat(row.minPrice?.exclVat || null)
        setMaxPriceExclVat(row.maxPrice?.exclVat || null)
        setElements(row.elements || [])
      }
    }
    const show = () => {
      loadData()
      modalRef.current.show()
    }

    const handleDismiss = () => {
      setName(null)
      setType(null)
      setStartDate(null)
      setExpiryDate(null)
      setMinPriceInclVat(null)
      setMaxPriceInclVat(null)
      setMinPriceExclVat(null)
      setMaxPriceExclVat(null)
      setElements([])
      setNameError(null)
      setVatError(null)
      setMinPriceExclVatError(null)
      setMaxPriceExclVatError(null)
      if (!row) setVat(null)
      setElementsError(false)
      setDateError(null)
      setPriceError(null)
      onDismiss?.()
    }

    const dismiss = () => {
      handleDismiss()
      modalRef.current.dismiss()
    }
    useImperativeHandle(ref, () => ({ show, dismiss }))

    const hasVat = vat !== '' && validator.isFloat(String(vat))
    useEffect(() => {
      const vatValue = hasVat ? parseFloat(String(vat)) : null
      if (vatValue !== null && minPriceExclVat !== '') {
        const minExcl = parseFloat(String(minPriceExclVat))
        if (!minPriceExclVatError)
          setMinPriceInclVat((minExcl * (1 + vatValue / 100)).toFixed(2))
      } else {
        setMinPriceInclVat('')
        setMinPriceExclVat('')
      }

      if (vatValue !== null && maxPriceExclVat !== '') {
        const maxExcl = parseFloat(String(maxPriceExclVat))
        if (!maxPriceExclVatError)
          setMaxPriceInclVat((maxExcl * (1 + vatValue / 100)).toFixed(2))
      } else {
        setMaxPriceInclVat('')
        setMaxPriceExclVat('')
      }
    }, [vat, minPriceExclVat, maxPriceExclVat, hasVat])

    const handleSubmit = () => {
      const newTariff = {}
      if (name) newTariff.name = name
      if (type) newTariff.type = type
      if (startDate) newTariff.startDate = startDate
      if (expiryDate) newTariff.expiryDate = expiryDate
      if (minPriceExclVat) {
        newTariff.minPrice = {
          exclVat: parseFloat(minPriceExclVat),
          inclVat: parseFloat(minPriceInclVat),
        }
      }
      if (maxPriceExclVat) {
        newTariff.maxPrice = {
          exclVat: parseFloat(maxPriceExclVat),
          inclVat: parseFloat(maxPriceInclVat),
        }
      }
      if (elements) {
        newTariff.elements = normalizeElements(elements)
      }

      onSubmit?.(newTariff)
    }
    const onDeleteClickHandler = () => {
      deleteModalRef.current.show()
    }

    const startDatePickerRef = useRef(null)
    const expiryDatePickerRef = useRef(null)
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
      const handleScroll = (e) => {
        const menuEl = document.querySelector('.rs-picker-menu')
        if (menuEl && menuEl.contains(e.target)) return

        if (menuOpen) {
          ;[startDatePickerRef, expiryDatePickerRef].forEach((pickerRef) => {
            if (pickerRef?.current?.close) {
              pickerRef.current.close()
              const wrapper = pickerRef.current?.root
              const toggle = wrapper?.querySelector('.rs-picker-toggle')
              if (toggle) {
                toggle.blur()
                toggle.classList.remove('rs-picker-toggle-active')
              }
            }
          })
          setMenuOpen(false)
        }
      }

      window.addEventListener('scroll', handleScroll, true)
      return () => {
        window.removeEventListener('scroll', handleScroll, true)
      }
    }, [menuOpen])
    return (
      <IFModal
        title={
          isEditing ? t('TariffModal.EditTitle') : t('TariffModal.CreateTitle')
        }
        ref={modalRef}
        maxWidth={60}
        dialogContentStyle={customDialogContentStyle}
        onDismiss={handleDismiss}
        style={{
          paddingRight: 0,
        }}
        exitIconStyle={{ marginRight: '1rem' }}
      >
        <div
          className={Styles.Container}
          onClick={(e) => e.stopPropagation()}
          ref={rootRef}
          data-overlayscrollbars=""
          style={{
            width: '100%',
            height: '100%',
            paddingRight: 16,
          }}
        >
          <div className={Styles.TariffContents}>
            <div className={Styles.Details}>
              <div className={Styles.InputItem}>
                <IFText
                  className={Styles.InputLabel}
                  style={{ color: Colors.DetailPanel }}
                >
                  {t('TariffModal.Name')}
                </IFText>
                <IFTextInput
                  className={Styles.TextInput}
                  value={name}
                  onChange={(e) => {
                    const value = e.target.value
                    setName(value)

                    if (validator.isEmpty(value.trim())) {
                      setNameError(t('TariffModal.Required'))
                    } else if (!validator.isLength(value.trim(), { min: 3 })) {
                      setNameError(
                        t('OperatorAlertList.NameMustBeAtLeastN', { n: 3 }),
                      )
                    } else {
                      setNameError(undefined)
                    }
                  }}
                  onBlur={() => {
                    const value = name ?? ''
                    if (validator.isEmpty(value.trim())) {
                      setNameError(t('TariffModal.Required'))
                    } else if (!validator.isLength(value.trim(), { min: 3 })) {
                      setNameError(
                        t('OperatorAlertList.NameMustBeAtLeastN', { n: 3 }),
                      )
                    } else {
                      setNameError()
                    }
                  }}
                  errorText={nameError}
                  placeholder={t('TariffModal.Name')}
                />
              </div>
              <div className={Styles.InputItemRight}>
                <IFText
                  className={Styles.InputLabel}
                  style={{ color: Colors.DetailPanel }}
                >
                  {t('TariffModal.Type')}
                </IFText>
                <IFSelect
                  menuMaxHeight={100}
                  data={
                    InfinityEnums?.TariffType
                      ? Object.values(InfinityEnums.TariffType).map((type) => ({
                          label: type,
                          value: type,
                        }))
                      : []
                  }
                  value={type}
                  onChange={(val) => setType(val)}
                  style={{ minWidth: '12rem', maxWidth: '12rem' }}
                  size="sm"
                  canLoadMore={false}
                  searchable={false}
                  cleanable={false}
                />
              </div>
            </div>
            <div className={Styles.Details}>
              <div className={Styles.InputItem}>
                <IFText
                  className={Styles.InputLabel}
                  style={{ color: Colors.DetailPanel }}
                >
                  {t('TariffModal.StartDate')}
                </IFText>
                <div className={Styles.FieldAbs}>
                  <DatePicker
                    className={Styles.DatePicker}
                    ref={startDatePickerRef}
                    value={startDate}
                    onChange={(val) => {
                      setStartDate(val)
                    }}
                    cleanable
                    size="sm"
                    placeholder="YYYY-MM-DD"
                    format="yyyy-MM-dd"
                    disabledDate={isPastDay}
                    style={{
                      minWidth: '12rem',
                      maxWidth: '12rem',
                      minHeight: '1.5rem',
                    }}
                    placement="autoVerticalStart"
                    onOpen={() => setMenuOpen(true)}
                    onClose={() => setMenuOpen(false)}
                  />
                  {dateError && (
                    <div className={Styles.ErrorText}>{dateError}</div>
                  )}
                </div>
              </div>
              <div className={Styles.InputItemRight}>
                <IFText
                  className={Styles.InputLabel}
                  style={{ color: Colors.DetailPanel }}
                >
                  {t('TariffModal.ExpiryDate')}
                </IFText>
                <div className={Styles.FieldAbs}>
                  <DatePicker
                    ref={expiryDatePickerRef}
                    className={Styles.DatePicker}
                    value={expiryDate}
                    onChange={(val) => {
                      setExpiryDate(val)
                    }}
                    cleanable
                    size="sm"
                    placeholder="YYYY-MM-DD"
                    format="yyyy-MM-dd"
                    disabledDate={isPastDay}
                    style={{
                      minWidth: '12rem',
                      maxWidth: '12rem',
                      minHeight: '1.5rem',
                    }}
                    placement="autoVerticalStart"
                    onOpen={() => setMenuOpen(true)}
                    onClose={() => setMenuOpen(false)}
                  />
                  {dateError && (
                    <div className={Styles.ErrorText}>{dateError}</div>
                  )}
                </div>
              </div>
            </div>
            <div
              className={Styles.VerticalDivider}
              style={{ backgroundColor: Colors.grey }}
            />
            <div className={Styles.Details}>
              <div className={Styles.InputItem}>
                <IFText
                  className={Styles.InputLabel}
                  style={{ color: Colors.DetailPanel }}
                >
                  {t('TariffModal.VAT')}
                </IFText>
                <IFTextInput
                  className={Styles.TextInput}
                  value={vat}
                  onChange={(e) => {
                    setVat(e.target.value)
                    if (e.target.value === '') {
                      setVatError(undefined)
                      return
                    }

                    if (!validator.isFloat(e.target.value)) {
                      setVatError(t('TariffModal.Invalid'))
                      return
                    }

                    if (!validator.isFloat(e.target.value, { min: 0.1 })) {
                      setVatError(t('TariffModal.Invalid'))
                      return
                    }
                    setVatError(undefined)
                  }}
                  errorText={vatError}
                  placeholder={t('TariffModal.Vat')}
                />
              </div>
              <div className={Styles.InputItemRight}>
                <IFText
                  className={Styles.InputLabel}
                  style={{ color: Colors.DetailPanel }}
                >
                  {t('TariffModal.MinPrice')}
                </IFText>

                <div className={Styles.InputField}>
                  <IFTextInput
                    className={Styles.TextInputSmall}
                    value={minPriceExclVat}
                    disabled={!hasVat ? true : false}
                    onChange={(e) => {
                      let value = e.target.value
                      if (value === '') {
                        setMinPriceExclVat(value)
                        setMinPriceExclVatError(undefined)
                        return
                      }
                      const decimalPlaces =
                        operator?.country?.currency?.decimalPlaces ?? 2
                      const decimalPattern = new RegExp(
                        `^\\d*\\.?\\d{0,${decimalPlaces}}$`,
                      )

                      if (!decimalPattern.test(value)) {
                        return
                      }

                      setMinPriceExclVat(value)

                      if (!validator.isFloat(value)) {
                        setMinPriceExclVatError(t('TariffModal.Invalid'))
                        return
                      }

                      if (!validator.isFloat(value, { min: 0.1 })) {
                        setMinPriceExclVatError(t('TariffModal.Invalid'))
                        return
                      }

                      setMinPriceExclVatError(undefined)
                    }}
                    errorText={minPriceExclVatError || priceError}
                    placeholder={t('TariffModal.ExclVat')}
                  />

                  <IFTextInput
                    className={Styles.TextInputSmall}
                    value={minPriceInclVat}
                    disabled
                    placeholder={t('TariffModal.InclVat')}
                  />
                </div>
              </div>
            </div>
            <div className={Styles.Details}>
              <div className={Styles.InputItemRightEnd}>
                <IFText
                  className={Styles.InputLabel}
                  style={{ color: Colors.DetailPanel }}
                >
                  {t('TariffModal.MaxPrice')}
                </IFText>
                <div className={Styles.InputField}>
                  <IFTextInput
                    className={Styles.TextInputSmall}
                    value={maxPriceExclVat}
                    disabled={!hasVat ? true : false}
                    onChange={(e) => {
                      const value = e.target.value
                      if (value === '') {
                        setMaxPriceExclVat(value)
                        setMaxPriceExclVatError(undefined)
                        return
                      }
                      const decimalPlaces =
                        operator?.country?.currency?.decimalPlaces ?? 2
                      const decimalPattern = new RegExp(
                        `^\\d*\\.?\\d{0,${decimalPlaces}}$`,
                      )

                      if (!decimalPattern.test(value)) {
                        return
                      }
                      setMaxPriceExclVat(value)
                      if (!validator.isFloat(value)) {
                        setMaxPriceExclVatError(t('TariffModal.Invalid'))
                        return
                      }

                      if (!validator.isFloat(value, { min: 0.1 })) {
                        setMaxPriceExclVatError(t('TariffModal.Invalid'))
                        return
                      }

                      setMaxPriceExclVatError(undefined)
                    }}
                    errorText={maxPriceExclVatError || priceError}
                    placeholder={t('TariffModal.ExclVat')}
                  />

                  <IFTextInput
                    className={Styles.TextInputSmall}
                    value={maxPriceInclVat}
                    disabled
                    placeholder={t('TariffModal.InclVat')}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={Styles.TariffElements}>
            <TariffElementsComponent
              onChange={(elements) => setElements(elements)}
              onErrorChange={(error) => setElementsError(error)}
              elements={row?.elements || []}
              operator={operator}
            />
          </div>
          <div className={Styles.ButtonsContainer}>
            {isEditing ? (
              <>
                <IFButton
                  color={Colors.red}
                  isFill
                  text={t('TariffModal.Delete')}
                  onClick={onDeleteClickHandler}
                  size="xsm"
                  className={Styles.Button}
                  isLoading={deleteTariffRequestState === RequestState.LOADING}
                />
                <IFButton
                  color={Colors.primary}
                  isFill
                  text={t('TariffModal.Save')}
                  onClick={handleSubmit}
                  size="xsm"
                  className={Styles.Button}
                  isDead={
                    !hasChanges ||
                    nameError ||
                    vatError ||
                    minPriceExclVatError ||
                    maxPriceExclVatError ||
                    elementsError ||
                    elements.length === 0 ||
                    !isFormValid(elements) ||
                    dateError ||
                    priceError
                  }
                  isLoading={updateTariffRequestState === RequestState.LOADING}
                />
              </>
            ) : (
              <IFButton
                color={Colors.primary}
                isFill
                text={t('TariffModal.Create')}
                onClick={handleSubmit}
                size="xsm"
                className={Styles.Button}
                isDead={
                  !name ||
                  nameError ||
                  vatError ||
                  minPriceExclVatError ||
                  maxPriceExclVatError ||
                  elementsError ||
                  elements.length === 0 ||
                  !isFormValid(elements) ||
                  dateError ||
                  priceError
                }
                isLoading={createTariffRequestState === RequestState.LOADING}
              />
            )}
          </div>
        </div>
        <IFDialog
          ref={deleteModalRef}
          dialogWidth="32rem"
          title={t('TariffModal.deleteModalTitle')}
          subtitle={t('TariffModal.deleteModalText')}
          buttonCancelColor={Colors.primary}
          actionButtonColor={Colors.red}
          actionButtonText={t('TariffModal.deleteModalConfirm')}
          buttonClassName={Styles.Button}
          isLoading={deleteTariffRequestState === RequestState.LOADING}
          onAction={handleDelete}
        />
      </IFModal>
    )
  },
)

TariffModal.propTypes = {
  onDismiss: PropTypes.func,
  onSubmit: PropTypes.func,
  createTariffRequestState: PropTypes.number,
  row: PropTypes.object,
  isEditing: PropTypes.bool,
  updateTariffRequestState: PropTypes.number,
  handleDelete: PropTypes.func,
  deleteTariffRequestState: PropTypes.number,
  operator: PropTypes.object,
}

export default TariffModal
